/**
 * Who may write what on the '콜봇 사용료' screen, decided from the two gate
 * reads: PW `GET /callbot-usage-fees/operator` (pack 8.2.1) and call-api
 * `GET /api/v1/callbot/billing/payments/health` (pack 7.3.3).
 *
 * Rules (AR doc 2, 7 and 15; pack 7.1, 7.4 note, 8.1):
 * 1. call-api health 404 (call-api refuses the token), a PW operator 404
 *    (the feature is off on PW or PW refuses the token), or an env mismatch
 *    (both answered 200 and `env` is not the same) turn every write off.
 * 2. Until both answered 200 with the same env, only the pause writes are on,
 *    each only when its own service answered: PW `POST /pause`, call-api
 *    `POST /payments/pause` and `POST /payments/months/:month/pause`.
 * 3. Outside the office (PW operator `officeIp` not true) the same three
 *    pauses stay on and nothing else: they are the only writes without the
 *    office check (pack 7.1, 8.1). hold/unhold, approval withdrawal and every
 *    resume need the office. call-api `viewer.office_ip` is not used; the
 *    screen follows the PW `officeIp` for both services (pack 7.4 note).
 * 4. PW mode (pack 8.1 table, AR doc 15): `approvalsOpen` false (off,
 *    handshake) turns off the header batch writes ([이 달 일괄 승인],
 *    [입금 여러 건 기록]) and the PW resume, which no row field covers. They
 *    stay visible, disabled with '결제 기능 꺼짐' (AR doc 15: '카드 승인·입금
 *    기록 버튼은 끈 채 보이고'; 2026-10-21: blocked PW buttons are not
 *    hidden). Resolving an unknown card transaction shows in live only. PW
 *    pause and approval withdrawal stay on.
 * Row writes (approvals, deposit writes, withdrawal, resolveUnknown) are also
 * bound by the row's `actionsAllowed` / `actions_allowed`; for them the server
 * carries the mode, and a blocked one shows disabled with its `actionsBlocked`
 * code text (pack 8.3, which wins over AR doc 15's 'hide'). The servers stay
 * the authority.
 *
 * Also here: the red banners and the header badges. The menu item and the
 * routes are open to every signed-in user like the other menus (user
 * decision 2026-10-28: no payment operator allowlist); the servers still
 * refuse other roles and impersonation tokens.
 *
 * Leaf module: type imports only.
 */
import type { CallApiHealth, PwOperatorResponse } from '../api/types';

/** Answer state of one gate read (PW operator check or call-api health). */
export type ServiceStatus = 'idle' | 'loading' | 'ok' | 'notFound' | 'error';

/** Red banner when both services answered with different env (AR doc 2). */
export const ENV_MISMATCH_TEXT = '환경 불일치';

/** What the gate reads from the PW operator body. */
export type OperatorRead = Pick<
  PwOperatorResponse,
  'env' | 'mode' | 'approvalsOpen' | 'officeIp'
>;

/** What the gate reads from the call-api health body. */
export interface HealthRead {
  env: CallApiHealth['env'];
}

export interface GateReads {
  /** PW `GET /callbot-usage-fees/operator`. */
  operatorStatus: ServiceStatus;
  /** Body of the last 200; null otherwise. */
  operator: OperatorRead | null;
  /** call-api `GET /api/v1/callbot/billing/payments/health`. */
  healthStatus: ServiceStatus;
  /** Body of the last 200; null otherwise. */
  health: HealthRead | null;
}

// ---------------------------------------------------------------------------
// Menu
// ---------------------------------------------------------------------------

/** Menu highlight: the section path itself or any path below it. */
export function isWithinSection(path: string, sectionPath: string): boolean {
  return path === sectionPath || path.startsWith(`${sectionPath}/`);
}

// ---------------------------------------------------------------------------
// Write gate
// ---------------------------------------------------------------------------

/** PW writes (contract 5, pack 8.2). */
export type PwWrite =
  /** `POST /callbot-usage-fees/pause` (whole PW side or some months). */
  | 'pause'
  /** `POST /callbot-usage-fees/resume`. */
  | 'resume'
  /** `POST .../months/:month/approvals`: charge, retry, refund. */
  | 'approve'
  /** `POST /callbot-usage-fees/batches`: [이 달 일괄 승인]. */
  | 'batchApprove'
  /** `POST .../approvals/:approvalId/withdraw`. */
  | 'withdrawApproval'
  /** `POST .../months/:month/unknown-resolutions`. */
  | 'resolveUnknown'
  /** `POST .../offline-entries` and `.../reversal`: receipt, bank refund, reversal, recovery. */
  | 'offlineEntry'
  /** `POST /callbot-usage-fees/offline-entries/batch`: [입금 여러 건 기록]. */
  | 'batchReceipts';

/** call-api writes (contract 4, pack 7.2). */
export type CallApiWrite =
  /** `POST /payments/pause` and `POST /payments/months/:month/pause`. */
  | 'pause'
  /** `POST /payments/resume` and `POST /payments/months/:month/resume`. */
  | 'resume'
  /** `PUT /payments/config`. */
  | 'config'
  /** `POST /payments/actions`: every action incl. hold/unhold, and multi-row acknowledge. */
  | 'actions'
  /** `PUT /payments/profiles`. */
  | 'profiles';

export const PW_WRITES: readonly PwWrite[] = [
  'pause',
  'resume',
  'approve',
  'batchApprove',
  'withdrawApproval',
  'resolveUnknown',
  'offlineEntry',
  'batchReceipts',
];

export const CALL_API_WRITES: readonly CallApiWrite[] = [
  'pause',
  'resume',
  'config',
  'actions',
  'profiles',
];

/**
 * PW writes the mode refuses while it is off or handshake (pack 8.1 table,
 * AR doc 15): card writes, resolving an unknown transaction, deposit writes
 * and the PW resume. PW pause and approval withdrawal work in every mode, and
 * the call-api writes do not depend on the PW mode (pack 7.1 has no mode
 * check).
 */
export const PW_MODE_GATED_WRITES: readonly PwWrite[] = [
  'approve',
  'batchApprove',
  'resolveUnknown',
  'offlineEntry',
  'batchReceipts',
  'resume',
];

/**
 * The write's dialog carries the '결제 기능 꺼짐' notice while the PW mode is off
 * or handshake (AR doc 결론 11, 15: on the card approval and deposit writes).
 */
export function modeGatesWrite(
  service: 'pw' | 'callApi',
  write: string
): boolean {
  return (
    service === 'pw' &&
    (PW_MODE_GATED_WRITES as readonly string[]).includes(write)
  );
}

/**
 * Why a write is off:
 * - allOff: health 404 (token refused), PW operator 404 (feature off or
 *   token refused) or env mismatch (red banner);
 * - notReady: the two gate reads have not both answered 200 with the same
 *   env, or (pauses) the write's own service has not answered;
 * - modeOff: the PW mode does not take the write (shown disabled,
 *   '결제 기능 꺼짐');
 * - liveOnly: resolving an unknown transaction outside live (hidden);
 * - outsideOffice: PW `officeIp` is not true ('사무실에서만 할 수 있습니다').
 */
export type WriteOffReason =
  | 'allOff'
  | 'notReady'
  | 'modeOff'
  | 'liveOnly'
  | 'outsideOffice';

export interface WriteState {
  /**
   * Show the button. False only for resolving an unknown transaction outside
   * live ('liveOnly'); a write the PW mode turns off stays visible, disabled.
   */
  visible: boolean;
  /** The button can be pressed. */
  enabled: boolean;
  /** Why it is off; null when enabled. */
  reason: WriteOffReason | null;
}

export interface WriteGate {
  /** Both answered 200 with the same env. */
  ready: boolean;
  /** Every write is off (health 404, PW operator 404 or env mismatch). */
  allOff: boolean;
  /** Both answered 200 and the env is not the same. */
  envMismatch: boolean;
  /** PW answered and `officeIp` is not true. */
  outsideOffice: boolean;
  pw: Readonly<Record<PwWrite, WriteState>>;
  callApi: Readonly<Record<CallApiWrite, WriteState>>;
}

interface GateFacts {
  pw: OperatorRead | null;
  health: HealthRead | null;
  pwEnv: string | null;
  callApiEnv: string | null;
  envMismatch: boolean;
  allOff: boolean;
  ready: boolean;
}

function objectOrNull<T>(value: T | null | undefined): T | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value
    : null;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function factsOf(reads: GateReads): GateFacts {
  const pw =
    reads.operatorStatus === 'ok' ? objectOrNull(reads.operator) : null;
  const health =
    reads.healthStatus === 'ok' ? objectOrNull(reads.health) : null;
  const pwEnv = pw ? nonEmptyString(pw.env) : null;
  const callApiEnv = health ? nonEmptyString(health.env) : null;
  // Both answered, but the env is not known to be the same (a missing env
  // counts as different): '환경 불일치'.
  const envMismatch =
    pw !== null && health !== null && (pwEnv === null || pwEnv !== callApiEnv);
  const allOff =
    reads.operatorStatus === 'notFound' ||
    reads.healthStatus === 'notFound' ||
    envMismatch;
  return {
    pw,
    health,
    pwEnv,
    callApiEnv,
    envMismatch,
    allOff,
    ready: pw !== null && health !== null && !envMismatch,
  };
}

function writeState(
  visible: boolean,
  reason: WriteOffReason | null
): WriteState {
  return { visible, enabled: reason === null, reason };
}

/**
 * PW writes the mode does not take (pack 8.1 table): 'modeOff' writes stay
 * visible and disabled; 'liveOnly' (resolving an unknown transaction) is
 * hidden unless the row's `actionsBlocked` names it (logic/rowActions).
 */
function pwModeBlock(
  write: PwWrite,
  pw: OperatorRead | null
): WriteOffReason | null {
  if (pw === null) return null; // mode unknown: nothing is hidden
  const approvalsOpen = pw.approvalsOpen === true;
  switch (write) {
    case 'batchApprove':
    case 'batchReceipts':
    case 'resume':
      return approvalsOpen ? null : 'modeOff';
    case 'resolveUnknown':
      return approvalsOpen && pw.mode === 'live' ? null : 'liveOnly';
    default:
      // pause and withdrawal: every mode. approve and offlineEntry: the row's
      // actionsAllowed / actionsBlocked carry the mode (pack 8.3).
      return null;
  }
}

function pwWriteState(write: PwWrite, f: GateFacts): WriteState {
  const mode = pwModeBlock(write, f.pw);
  const visible = mode !== 'liveOnly';
  if (f.allOff) return writeState(visible, 'allOff');
  if (write === 'pause') return writeState(visible, f.pw ? null : 'notReady');
  if (!f.ready) return writeState(visible, 'notReady');
  if (mode !== null) return writeState(visible, mode);
  if (f.pw?.officeIp !== true) return writeState(visible, 'outsideOffice');
  return writeState(visible, null);
}

function callApiWriteState(write: CallApiWrite, f: GateFacts): WriteState {
  if (f.allOff) return writeState(true, 'allOff');
  if (write === 'pause') {
    return writeState(true, f.health ? null : 'notReady');
  }
  if (!f.ready) return writeState(true, 'notReady');
  if (f.pw?.officeIp !== true) return writeState(true, 'outsideOffice');
  return writeState(true, null);
}

function stateMap<K extends string>(
  keys: readonly K[],
  stateOf: (key: K) => WriteState
): Record<K, WriteState> {
  const out = {} as Record<K, WriteState>;
  for (const key of keys) out[key] = stateOf(key);
  return out;
}

/** Write gating from the two gate reads (rules in the module comment). */
export function writeGate(reads: GateReads): WriteGate {
  const f = factsOf(reads);
  return {
    ready: f.ready,
    allOff: f.allOff,
    envMismatch: f.envMismatch,
    outsideOffice: f.pw !== null && f.pw.officeIp !== true,
    pw: stateMap(PW_WRITES, (write) => pwWriteState(write, f)),
    callApi: stateMap(CALL_API_WRITES, (write) => callApiWriteState(write, f)),
  };
}

// ---------------------------------------------------------------------------
// Banners and badges of the page header
// ---------------------------------------------------------------------------

/**
 * Red banners (AR doc 2):
 * - pwFeatureOff: operator check 404, the feature is off on PW or PW
 *   refuses the token (errors.ts PW_FEATURE_OFF_TEXT);
 * - callApiRefused: health 404, the call-api operator guard refuses the
 *   token (errors.ts CALL_API_NOT_FOUND_TEXT);
 * - envMismatch: '환경 불일치';
 * - pwNoAnswer: operator check failed with anything but 404,
 *   '결제 서버(PW) 응답 없음';
 * - callApiNoAnswer: health failed with anything but 404, 'call-api 응답 없음'.
 */
export type SessionBannerCode =
  | 'pwFeatureOff'
  | 'callApiRefused'
  | 'envMismatch'
  | 'pwNoAnswer'
  | 'callApiNoAnswer';

/** The red banners to show, in the order of the codes above. */
export function sessionBanners(reads: GateReads): SessionBannerCode[] {
  const f = factsOf(reads);
  const out: SessionBannerCode[] = [];
  if (reads.operatorStatus === 'notFound') out.push('pwFeatureOff');
  if (reads.healthStatus === 'notFound') out.push('callApiRefused');
  if (f.envMismatch) out.push('envMismatch');
  if (reads.operatorStatus === 'error') out.push('pwNoAnswer');
  if (reads.healthStatus === 'error') out.push('callApiNoAnswer');
  return out;
}

export interface HeaderBadges {
  /** PW operator `env` for the env badge (AR doc 15); null until PW answered. */
  env: string | null;
  /** PW mode dry_run: '카드: 모의 · 입금 기록: 실제' (AR doc 15). */
  dryRun: boolean;
  /** PW answered with `approvalsOpen` not true (off, handshake): '결제 기능 꺼짐'. */
  paymentOff: boolean;
}

export function headerBadges(reads: GateReads): HeaderBadges {
  const f = factsOf(reads);
  return {
    env: f.pwEnv,
    dryRun: f.pw?.mode === 'dry_run',
    paymentOff: f.pw !== null && f.pw.approvalsOpen !== true,
  };
}
